// src/pages/lab/protocols/components/stepEditingComponents/StepEquipmentEditor.tsx
import React, { useState } from "react";
import { Table, Button, Drawer, Input, Select, Space, Tooltip, message } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, ToolOutlined } from "@ant-design/icons";

import { Equipment, EquipmentTypeOptions } from "../../../../../../types/lab/ProtocolTypes";

interface StepEquipmentEditorProps {
	equipment: Equipment[];
	onEquipmentChange: (equipment: Equipment[]) => void;
}

const StepEquipmentEditor: React.FC<StepEquipmentEditorProps> = ({ equipment, onEquipmentChange }) => {
	const [editingEquipment, setEditingEquipment] = useState<Partial<Equipment>>({ type: "", name: "" });
	const [equipmentDrawerVisible, setEquipmentDrawerVisible] = useState(false);

	const handleAddEquipment = () => {
		setEditingEquipment({ type: "", name: "" });
		setEquipmentDrawerVisible(true);
	};

	const handleEditEquipment = (eq: Equipment) => {
		setEditingEquipment(eq);
		setEquipmentDrawerVisible(true);
	};

	const handleDeleteEquipment = (equipmentName: string) => {
		const updatedEquipment = equipment.filter((eq) => eq.name !== equipmentName);
		onEquipmentChange(updatedEquipment);
	};

	const handleSaveEquipment = () => {
		if (!editingEquipment.type?.trim() || !editingEquipment.name?.trim()) {
			message.error("Equipment type and name are required");
			return;
		}

		const updatedEquipment = [...equipment];
		const existingIndex = updatedEquipment.findIndex((eq) => eq.name === editingEquipment.name);

		if (existingIndex >= 0) {
			updatedEquipment[existingIndex] = editingEquipment as Equipment;
		} else {
			updatedEquipment.push(editingEquipment as Equipment);
		}

		onEquipmentChange(updatedEquipment);
		setEquipmentDrawerVisible(false);
		setEditingEquipment({ type: "", name: "" });
	};

	const updateEquipmentProperty = (property: string, value: any) => {
		setEditingEquipment((prev) => ({ ...prev, [property]: value }));
	};

	const columns = [
		{
			title: "Equipment Name",
			dataIndex: "name",
			key: "name",
			render: (text: string) => <span style={{ fontSize: "12px", fontWeight: 500 }}>{text}</span>,
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 150,
			render: (type: string) => {
				const typeOption = EquipmentTypeOptions.find((opt) => opt.value === type);
				return (
					<span
						style={{
							fontSize: "10px",
							padding: "2px 8px",
							borderRadius: "4px",
							backgroundColor: "rgba(24, 144, 255, 0.1)",
							color: "#1890ff",
						}}
					>
						{typeOption?.label || type}
					</span>
				);
			},
		},
		{
			title: "Actions",
			key: "actions",
			width: 100,
			render: (record: Equipment) => (
				<Space size={4}>
					<Tooltip title="Edit Equipment">
						<Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditEquipment(record)} />
					</Tooltip>
					<Tooltip title="Remove Equipment">
						<Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteEquipment(record.name)} />
					</Tooltip>
				</Space>
			),
		},
	];

	return (
		<div>
			{/* Header */}
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
				<p style={{ fontSize: "11px", color: "var(--text-color-secondary)", margin: 0 }}>Specify equipment required for this step</p>
				<Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddEquipment}>
					Add Equipment
				</Button>
			</div>

			{/* Equipment Table */}
			{equipment.length > 0 ? (
				<Table columns={columns} dataSource={equipment} rowKey="name" pagination={false} size="small" style={{ fontSize: "11px" }} />
			) : (
				<div
					style={{
						textAlign: "center",
						padding: "32px 16px",
						border: "2px dashed var(--border-color)",
						borderRadius: "8px",
						backgroundColor: "var(--background-color)",
					}}
				>
					<ToolOutlined style={{ fontSize: "32px", color: "var(--text-color-secondary)", marginBottom: 8 }} />
					<p style={{ fontSize: "11px", color: "var(--text-color-secondary)", margin: 0 }}>No equipment requirements defined</p>
				</div>
			)}

			{/* Equipment Editor Drawer */}
			<Drawer
				title={
					<Space size={8}>
						<ToolOutlined style={{ fontSize: "14px" }} />
						<span style={{ fontSize: "14px" }}>{editingEquipment.name ? "Edit Equipment" : "Add Equipment"}</span>
					</Space>
				}
				open={equipmentDrawerVisible}
				onClose={() => setEquipmentDrawerVisible(false)}
				width={400}
				footer={
					<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
						<Button icon={<CloseOutlined />} onClick={() => setEquipmentDrawerVisible(false)} size="small">
							Cancel
						</Button>
						<Button type="primary" icon={<SaveOutlined />} onClick={handleSaveEquipment} disabled={!editingEquipment.type?.trim() || !editingEquipment.name?.trim()} size="small">
							Save
						</Button>
					</div>
				}
			>
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					<div>
						<label
							style={{
								display: "block",
								marginBottom: 4,
								fontSize: "11px",
								fontWeight: 500,
							}}
						>
							Equipment Type *
						</label>
						<Select value={editingEquipment.type} options={EquipmentTypeOptions} onChange={(value) => updateEquipmentProperty("type", value)} placeholder="Select equipment type" size="small" style={{ width: "100%" }} />
					</div>

					<div>
						<label
							style={{
								display: "block",
								marginBottom: 4,
								fontSize: "11px",
								fontWeight: 500,
							}}
						>
							Equipment Name *
						</label>
						<Input value={editingEquipment.name || ""} onChange={(e) => updateEquipmentProperty("name", e.target.value)} placeholder="Specific equipment name/model" size="small" />
					</div>
				</Space>
			</Drawer>
		</div>
	);
};

export default StepEquipmentEditor;
